import React, { useState } from 'react';
import { adminApi } from '../../lib/adminApi';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Lock, ArrowLeft, Eye, EyeOff } from 'lucide-react';


const formatError = (detail) => {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg).join(' ');
  return 'No se pudo iniciar sesión. Intenta nuevamente.';
};

export const AdminLogin = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await adminApi.post('/auth/login', { password });
      onLogin();
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-blue-600/30 blur-3xl"></div>
      <div className="absolute -bottom-40 -right-40 w-[520px] h-[520px] rounded-full bg-red-500/20 blur-3xl"></div>

      <div className="relative w-full max-w-md" data-testid="admin-login-card">
        <Link to="/" data-testid="admin-back-home-link" className="inline-flex items-center text-slate-400 hover:text-white text-sm mb-6 transition-colors">
          <ArrowLeft className="h-4 w-4 mr-2" /> Volver a la página
        </Link>

        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-2xl">
          <div className="flex items-center space-x-3 mb-8">
            <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-400 flex items-center justify-center shadow-lg">
              <Lock className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold leading-none">
                <span className="text-blue-400">IWANNA</span>{' '}
                <span className="text-red-400 text-lg font-medium">insurance</span>
              </p>
              <p className="text-slate-400 text-sm mt-1">Panel privado de leads</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="admin-password" className="block text-sm text-slate-300 mb-2">Contraseña de acceso</label>
              <div className="relative">
                <Input
                  id="admin-password"
                  data-testid="admin-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="h-12 bg-slate-900/70 border-white/10 text-white placeholder:text-slate-500 rounded-xl pr-12 focus-visible:ring-blue-500"
                />
                <button
                  type="button"
                  data-testid="admin-toggle-password"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                  aria-label="Mostrar contraseña"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {error && (
              <p data-testid="admin-login-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
                {error}
              </p>
            )}

            <Button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loading || !password}
              className="w-full h-12 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white font-semibold shadow-lg transition-all"
            >
              {loading ? 'Verificando...' : 'Entrar al panel'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
